import React from "react"
import Link from "react-router-dom/Link"
import find from "lodash/find"
import get from "lodash/get"
import { constants, currentCompany } from "./constants"

const glossaryTagsInfo = (data, props) => {
    const { glossaryTags, toggleInfo } = props
    const glossaryTag = glossaryTags[data.type]

    const currentTag = find(glossaryTag, { glossary_id: Number(data.glossary_id) })
    const d = {
        name: currentTag.replace_with,
        description: currentTag.meaning,
    }
    toggleInfo(d)
}

export const glossaryTagsParseOption = props => ({
    replace: (domNode) => {
        const thisAttribs = domNode.attribs
        if (thisAttribs && thisAttribs.class && thisAttribs.class.indexOf("gTags") >= 0) {
            return (
                <button
                    type="button"
                    className="gTags"
                    onClick={() => glossaryTagsInfo(thisAttribs, props)}
                >
                    {domNode.children.map(v => v.data)}
                </button>
            )
        }
        if (thisAttribs && thisAttribs.class && thisAttribs.class.indexOf("pageTag") >= 0) {
            return (
                <Link to={thisAttribs.link} className="linkified">
                    {domNode.children.map(v => v.data)}
                </Link>
            )
        }
        return domNode
    },
})

/**
 * Finds glossary tags in the provided text and replaces them with links.
 * @param {string} data - The input text containing glossary tags.
 * @param {string} type - The type of glossary tags to search ("name" or "description").
 * @param {object} props - Additional properties, including glossaryTags.
 * @returns {string} - The text with replaced glossary tag links.
 */
export const findGlossaryTagsInText = (data, type, props) => {
    // Extract glossaryTags from props
    const { glossaryTags } = props;

    // Define regex pattern for glossary tags
    const regex = /g:(.*?):g/gmi;

    // Initialize description variable
    let description = null;

    // Get the appropriate tags based on the type
    const tags = type === "name" ? glossaryTags?.name : glossaryTags?.description;

    // eslint-disable-next-line no-cond-assign
    while ((description = regex.exec(data)) !== null) {
        // Find the corresponding glossary tag
        const des = find(tags, { word: description[0] });

        // If a match is found, replace the tag with a link
        if (des) {
            // eslint-disable-next-line no-param-reassign
            data = data.replace(description[0], `<a class="gTags" type="${type}" glossary_id="${des.glossary_id}">${des.replace_with}</a>`);
        }
    }

    return data;
};


export const removeSpecialCharactersWithUnderscore = str => str.trim().replace(/[^A-Z0-9]+/gi, "_").toLowerCase()

export const findTagsInText = (tagType, data, type, tags, selectedCourse) => {
    const { tagsType } = constants
    let regex = null
    if (tagType === tagsType.page) {
        regex = /pl\((.*?)\):(.*?):pl/g
    } else if (tagType === tagsType.glossary) {
        regex = /gl\((.*?)\):(.*?):gl/g
    } else if (tagType === tagsType.peopleFinder) {
        regex = /pfl\((.*?)\):(.*?):pfl/g
    } else if (tagType === tagsType.faq) {
        regex = /fl\((.*?)\):(.*?):fl/g
    }

    let description = null

    const tagsData = get(tags, type)

    // eslint-disable-next-line no-cond-assign
    while ((description = regex.exec(data)) !== null) {
        const des = find(tagsData, { word: description[0] })
        if (des) {
            let link = `/courses/${selectedCourse}`
            if (tagType === tagsType.page) {
                const storyId = get(des, "story_id")
                const chapterId = get(des, "chapter_id")
                const pageId = get(des, "page_id")
                link = `${link}/stories/${storyId}/chapters/${chapterId}/${pageId}`
            } else if (tagType === tagsType.glossary) {
                const informationCategoryId = get(des, "information_category_id")
                const glossaryId = get(des, "glossary_id")
                link = `${link}/knowledge/${informationCategoryId}/${glossaryId}`
            } else if (tagType === tagsType.peopleFinder) {
                const informationCategoryId = get(des, "information_category_id")
                const personId = get(des, "person_id")
                link = `${link}/knowledge/${informationCategoryId}/${personId}`
            } else if (tagType === tagsType.faq) {
                const informationCategoryId = get(des, "information_category_id")
                const faqId = get(des, "faq_id")
                link = `${link}/knowledge/${informationCategoryId}/${faqId}`
            }
            // eslint-disable-next-line no-param-reassign
            data = data.replace((description[0]), `<span class="pageTag" link="${link}">${des.replace_with}</span>`)
        }
    }
    return data
}

export const tagsParseOption = () => ({
    replace: (domNode) => {
        const thisAttribs = domNode.attribs
        if (thisAttribs && thisAttribs.class && thisAttribs.class.indexOf("pageTag") >= 0) {
            return (
                <Link to={thisAttribs.link} className="linkified">
                    {domNode.children.map(v => v.data)}
                </Link>
            )
        }
        return domNode
    },
})

export const isValidUrl = (url) => {
    let urlObject = null

    try {
        urlObject = new URL(url)
    } catch (error) {
        return false
    }

    const { protocol } = urlObject
    const protocolPosition = url.lastIndexOf(protocol)
    const domainExtensionPosition = url.lastIndexOf(".")

    return (
        protocolPosition === 0
        && ["http:", "https:"].indexOf(protocol) !== -1
        && domainExtensionPosition > 2 && url.length - domainExtensionPosition > 2
    )
}

export const getPrivacyUrl = () => {
    const {
        EPPolicyUrl, capgeminiPolicyUrl, telekomPolicyUrlEN,
        telekomPolicyUrlDE, privacyPolicyUrl, heinekenPolicyURL,
    } = constants

    let privacyUrl

    const subDomain = window.location.host.split(".")[0]
    const isTelekom = !!subDomain && subDomain === "telekom"
    const language = document.documentElement.lang

    if (currentCompany === "ep") {
        privacyUrl = EPPolicyUrl
    } else if (currentCompany === "capgemini") {
        privacyUrl = capgeminiPolicyUrl
    } else if (currentCompany === "heineken") {
        privacyUrl = heinekenPolicyURL
    } else if (isTelekom && language.toLowerCase() === "en") {
        privacyUrl = telekomPolicyUrlEN
    } else if (isTelekom && language.toLowerCase() === "de") {
        privacyUrl = telekomPolicyUrlDE
    } else {
        privacyUrl = privacyPolicyUrl
    }

    return privacyUrl
}

/**
 * Handles arrow key navigation within a set of focusable elements.
 *
 * @param {Event} event - The keyboard event object.
 * @param {React.RefObject} targetRef - The reference to the currently focused element.
 * @param {string[]} keyValues - An array of key values to trigger navigation (e.g., ["ArrowLeft", "ArrowRight"]).
 */
export const handleArrowKeyNavigation = (event, targetRef, keyValues) => {

    // Check and handle the case for nested elements
    if (!targetRef.current) {
        // If the current targetRef is undefined, assign it to itself
        targetRef.current = targetRef;
    }

    /**
     * Checks if the pressed key is included in the specified key values.
     * If true, prevents the default arrow key behavior and moves focus to the next element.
     */
    if (keyValues.includes(event.key)) {
        // Prevent default arrow key behavior (e.g., scrolling the page)
        event.preventDefault();

        // Move focus to the next element
        const focusableElements = document.querySelectorAll('[tabindex="0"]');
        const currentIndex = Array.from(focusableElements).indexOf(targetRef.current);
        const direction = event.key === keyValues[1] ? 1 : -1;
        const nextIndex = (currentIndex + direction + focusableElements.length) % focusableElements.length;

        focusableElements[nextIndex].focus();
    }
}

/**
 * Trims leading and trailing whitespaces from the provided text.
 *
 * @param {string} text - The input text to be trimmed.
 * @returns {string} - The trimmed text.
 */
export const trimText = (text) => {
    return String(text).trim();
};